import type { HelpGuide } from '../types';

export const masterGuide: HelpGuide = {
  key: 'master',
  title: 'Master Data (ข้อมูลหลัก)',
  subtitle: 'เมนู: ระบบ ▸ Master Data',
  updated: 'ปรับปรุง 20 มิ.ย. 2569',
  docxUrl: '/help/master/คู่มือการใช้งาน_Master-Data_DCC-ERP.docx',
  sections: [
    {
      id: 'overview',
      heading: '1. หน้านี้คืออะไร และใช้ทำอะไร',
      blocks: [
        {
          kind: 'para',
          text: 'หน้า “Master Data (ข้อมูลหลัก)” คือศูนย์รวมข้อมูลตั้งต้นของทั้งระบบ — ผังบัญชี บริษัทในเครือ สาขา หน่วยงาน พนักงาน ผู้ขาย ลูกค้า บัญชีธนาคาร ทรัพย์สิน เงินกู้ เลขเอกสาร งวดบัญชี และสกุลเงิน ข้อมูลเหล่านี้ถูกนำไปใช้อ้างอิงในทุกหน้าจอของระบบ (บันทึกบัญชี ตั้งหนี้ รับชำระ ฯลฯ) จึงควรกำหนดให้ถูกต้องครบถ้วนก่อนเริ่มใช้งาน',
        },
        {
          kind: 'callout',
          text: 'หลักการสำคัญ: ข้อมูลทุกตารางแยกตาม “บริษัท” ที่กำลังเลือกใช้งานอยู่ (DCC / TTOP / RCI) ยกเว้น บริษัทในเครือ และ สกุลเงิน ที่ใช้ร่วมกันทั้งระบบ — เมื่อเปลี่ยนบริษัทที่ทำงาน ข้อมูลในตารางจะเปลี่ยนตาม',
          variant: 'info',
        },
        { kind: 'para', text: 'เมนูซ้ายแบ่งกลุ่มข้อมูลหลักเป็น 4 หมวด:' },
        {
          kind: 'bullets',
          items: [
            {
              lead: 'หลัก — ',
              text: 'ผังบัญชี · บริษัทในเครือ · สาขา · หน่วยงาน / แผนก · Project / Cost Center',
            },
            {
              lead: 'คน + คู่ค้า — ',
              text: 'พนักงาน · ผู้ขาย (Vendor) · ลูกค้า · เงื่อนไขชำระ (Payment Terms)',
            },
            { lead: 'ทรัพยากร — ', text: 'ธนาคารและบัญชี · ทรัพย์สิน Master · เงินกู้บริษัท' },
            { lead: 'ระบบ — ', text: 'เลขเอกสาร · ปีบัญชี & งวด · สกุลเงิน & FX Rate' },
          ],
        },
        {
          kind: 'image',
          src: '/help/master/01-overview-coa.png',
          caption:
            'หน้าหลัก Master Data — เมนูเลือกประเภทข้อมูลด้านซ้าย, แถบค้นหา/กรอง, ปุ่ม Import/Export/เพิ่ม และตารางผังบัญชี',
        },
      ],
    },
    {
      id: 'anatomy',
      heading: '2. ส่วนประกอบของหน้าจอ',
      blocks: [
        { kind: 'para', text: 'แต่ละหน้าจอประกอบด้วยส่วนหลักดังนี้:' },
        {
          kind: 'bullets',
          items: [
            {
              lead: 'เมนูซ้าย — ',
              text: 'เลือกประเภทข้อมูลหลักที่จะดู/แก้ไข (จัดเป็น 4 หมวดตามข้างต้น)',
            },
            {
              lead: 'ปุ่มมุมขวาบน — ',
              text: 'Import จาก Excel (นำเข้าทีละหลายรายการ) · Export (ดาวน์โหลด) · + เพิ่ม {ชื่อข้อมูล} (สร้างรายการใหม่)',
            },
            {
              lead: 'แถบค้นหา — ',
              text: 'ช่อง “ค้นหา...” (ค้นจากรหัส + ชื่อ) เป็นตัวกรองที่ทำงานจริง · ตัวเลือก สถานะ (ใช้งาน/ปิดใช้งาน/ทั้งหมด) และ เรียงตาม (รหัส/ชื่อ/วันสร้าง) เป็นเพียงตัวเลือกแสดงผลที่ยังไม่ทำงาน · ปุ่ม Activity Log: ดูประวัติ',
            },
            {
              lead: 'ตารางข้อมูล — ',
              text: 'แสดงรายการตามประเภทที่เลือก แต่ละแถวมีไอคอน ตั้งค่า (แก้ไข) และ X (ปิดใช้งาน/ลบ) ในคอลัมน์การกระทำ',
            },
          ],
        },
        {
          kind: 'callout',
          text: 'ผังบัญชี · บริษัทในเครือ · สกุลเงิน เป็นข้อมูลแบบดูอย่างเดียว/แก้ไขจำกัด (ผังบัญชีและบริษัทไม่มีปุ่มแก้ไขในหน้านี้ · สกุลเงินแก้ไขได้เฉพาะชื่อสกุลเงิน — อัตราแลกเปลี่ยนเป็นแบบดูอย่างเดียว แก้ไขด้วยมือไม่ได้)',
          variant: 'warn',
        },
      ],
    },
    {
      id: 'company',
      heading: '3. บริษัทในเครือ',
      blocks: [
        {
          kind: 'para',
          text: 'แสดงบริษัทในกลุ่ม 3 บริษัท (DCC / TTOP / RCI) เป็นข้อมูลแบบดูอย่างเดียว แต่ละแถวแสดง: รหัส · ชื่อภาษาไทย · ชื่อภาษาอังกฤษ · เลขผู้เสียภาษี · ธีมสี (แถบสี + รหัสสี) · สถานะ ใช้ตรวจสอบเลขผู้เสียภาษีและสีประจำบริษัทที่ใช้แยกข้อมูลทั้งระบบ',
        },
        {
          kind: 'image',
          src: '/help/master/02-company.png',
          caption:
            'บริษัทในเครือ — DCC / TTOP / RCI พร้อมชื่อไทย-อังกฤษ เลขผู้เสียภาษี และธีมสีประจำบริษัท',
        },
      ],
    },
    {
      id: 'branch',
      heading: '4. สาขา',
      blocks: [
        {
          kind: 'para',
          text: 'ทะเบียนสาขาของบริษัท แต่ละแถวแสดง: รหัส · ชื่อสาขา · ลำดับที่ของสาขา (เลข 5 หลักตามที่จดทะเบียนกับสรรพากร — 00000 = สำนักงานใหญ่) · ที่อยู่ · สถานะ ใช้ไอคอนตั้งค่าเพื่อแก้ไข หรือ X เพื่อปิดใช้งาน',
        },
        {
          kind: 'image',
          src: '/help/master/03-branch.png',
          caption: 'รายการสาขา — รหัส, ชื่อสาขา, ลำดับที่ของสาขา (5 หลัก) และที่อยู่',
        },
      ],
    },
    {
      id: 'branch-add',
      heading: '5. การเพิ่ม / แก้ไขสาขา',
      blocks: [
        {
          kind: 'para',
          text: 'กดปุ่ม + เพิ่มสาขา (หรือไอคอนตั้งค่าที่แถวเพื่อแก้ไข) ระบบเปิดฟอร์มให้กรอก: รหัสสาขา (แก้ไขรหัสเดิมไม่ได้ตอนแก้ไข) · ลำดับที่ของสาขา 5 หลัก (ต้องเป็นตัวเลข 5 หลัก, 00000 = สำนักงานใหญ่) · ชื่อสาขา · ที่อยู่ และติ๊ก “ใช้งานอยู่” แล้วกด บันทึก',
        },
        {
          kind: 'image',
          src: '/help/master/04-branch-modal.png',
          caption: 'ฟอร์มเพิ่มสาขา — กรอกรหัส, ลำดับที่ของสาขา 5 หลัก, ชื่อ และที่อยู่',
        },
      ],
    },
    {
      id: 'employee',
      heading: '6. พนักงาน',
      blocks: [
        {
          kind: 'para',
          text: 'ทะเบียนพนักงาน ใช้อ้างอิงในงานเงินเดือน เบิกจ่าย และเงินทดรองจ่าย แต่ละแถวแสดง: รหัส · ชื่อ-สกุล · แผนก · ตำแหน่ง · Email · วันเข้าทำงาน · บัญชีธนาคาร · สถานะ ส่วนข้อมูลแผนก ตำแหน่ง อีเมล วันเข้าทำงาน และบัญชีธนาคารเป็นแบบดูอย่างเดียวในตาราง — ฟอร์มเพิ่ม/แก้ไขกรอกได้เฉพาะ รหัส + ชื่อ',
        },
        {
          kind: 'image',
          src: '/help/master/05-employee.png',
          caption:
            'ทะเบียนพนักงาน — แผนก, ตำแหน่ง, อีเมล, วันเข้าทำงาน และบัญชีธนาคารสำหรับโอนเงิน',
        },
      ],
    },
    {
      id: 'vendor',
      heading: '7. ผู้ขาย (Vendor)',
      blocks: [
        {
          kind: 'para',
          text: 'รายชื่อผู้ขาย/เจ้าหนี้ ใช้ในงานตั้งหนี้และจ่ายเงิน (AP) แต่ละแถวแสดง: รหัส · ชื่อผู้ขาย · เลขผู้เสียภาษี · ประเทศ · Payment Terms (เงื่อนไขชำระ) · Email · สกุลเงิน (ถ้าไม่ใช่ THB จะแสดงป้ายเตือน) · สถานะ',
        },
        {
          kind: 'image',
          src: '/help/master/06-vendor.png',
          caption:
            'รายชื่อผู้ขาย — เลขผู้เสียภาษี, Payment Terms และสกุลเงิน (ป้ายเตือนเมื่อไม่ใช่ THB)',
        },
      ],
    },
    {
      id: 'customer',
      heading: '8. ลูกค้า',
      blocks: [
        {
          kind: 'para',
          text: 'ทะเบียนลูกค้า ใช้ในงานออกใบแจ้งหนี้และรับชำระ (AR) แต่ละแถวแสดง: รหัส · ชื่อลูกค้า · เลขผู้เสียภาษี · Payment Terms · Credit Limit (วงเงินเครดิต) · ยอดขาย YTD · การใช้วงเงิน (แถบความคืบหน้า เขียว/ส้ม/แดง ตามสัดส่วนที่ใช้ไป) · สถานะ',
        },
        {
          kind: 'image',
          src: '/help/master/07-customer.png',
          caption: 'ทะเบียนลูกค้า — วงเงินเครดิต, ยอดขาย YTD และแถบแสดงการใช้วงเงิน',
        },
      ],
    },
    {
      id: 'bank',
      heading: '9. ธนาคารและบัญชี',
      blocks: [
        {
          kind: 'para',
          text: 'บัญชีธนาคารของบริษัท ใช้ในงานรับ-จ่ายเงินและกระทบยอดธนาคาร แต่ละแถวแสดง: รหัส · ธนาคาร · เลขที่บัญชี · ชื่อบัญชี · ประเภท (กระแสรายวัน/ออมทรัพย์/ฝากประจำ/เงินตราต่างประเทศ) · สาขา · ยอดคงเหลือ · สถานะ',
        },
        {
          kind: 'image',
          src: '/help/master/08-bank.png',
          caption: 'บัญชีธนาคารบริษัท — ประเภทบัญชี, สาขา และยอดคงเหลือ',
        },
      ],
    },
    {
      id: 'bank-add',
      heading: '10. การเพิ่มบัญชีธนาคาร + ผูกบัญชี GL',
      blocks: [
        {
          kind: 'para',
          text: 'กดปุ่ม + เพิ่มบัญชีธนาคาร แล้วกรอก: รหัสบัญชี · ธนาคาร · เลขที่บัญชี · ประเภทบัญชี · ชื่อบัญชี · บัญชีแยกประเภท GL (เงินฝากธนาคาร) — เลือกรหัสบัญชีในผังบัญชีที่จะให้รายการของบัญชีนี้ลงบัญชีถูกต้อง · สาขา · ยอดยกมา และติ๊ก “ใช้งานอยู่”',
        },
        {
          kind: 'callout',
          text: 'ช่อง “บัญชีแยกประเภท GL” จำเป็นต้องเลือก — เพื่อให้ทุกรายการที่ผ่านบัญชีธนาคารนี้บันทึกเข้าบัญชีในผังบัญชีได้ถูกต้องโดยอัตโนมัติ',
          variant: 'warn',
        },
        {
          kind: 'image',
          src: '/help/master/09-bank-modal.png',
          caption: 'ฟอร์มเพิ่มบัญชีธนาคาร — เลือกประเภท, ผูกบัญชีแยกประเภท GL และกรอกยอดยกมา',
        },
      ],
    },
    {
      id: 'asset',
      heading: '11. ทรัพย์สิน Master',
      blocks: [
        {
          kind: 'para',
          text: 'ทะเบียนทรัพย์สินหลัก (อาคาร/ป้าย/ที่ดิน/เครื่องจักร/ยานพาหนะ) ใช้อ้างอิงงานภาษีโรงเรือน/ป้ายและทรัพย์สิน แต่ละแถวแสดง: รหัส · ประเภท · ชื่อ · ที่ตั้ง · ขนาด · มูลค่า · หน่วยงาน · สถานะ',
        },
        {
          kind: 'image',
          src: '/help/master/10-asset.png',
          caption: 'ทรัพย์สิน Master — ประเภท, มูลค่า และหน่วยงานที่รับผิดชอบ',
        },
      ],
    },
    {
      id: 'period',
      heading: '12. ปีบัญชี & งวด (การล็อกงวด)',
      blocks: [
        {
          kind: 'para',
          text: 'งวดบัญชีควบคุมว่าเดือนไหน “เปิด” ให้บันทึกบัญชีได้ และเดือนไหน “ปิด” แล้ว แต่ละแถวแสดง: งวด (เดือน/ปี) · ชื่อ · สถานะ · ผู้ปิดงวด · วันที่ปิด ปุ่มที่แถวจะเปลี่ยนตามสถานะ: งวดที่เปิดอยู่มีปุ่ม “ปิดงวด” · งวดที่ปิดแล้วมีปุ่ม “เปิดงวดใหม่” · งวดที่ยังไม่เปิดมีปุ่ม “เปิดงวด”',
        },
        {
          kind: 'callout',
          text: 'การโพสต์บัญชีทุกระบบจะรับเฉพาะงวดที่สถานะ “เปิดอยู่” เท่านั้น — ปิดงวดเมื่อปิดบัญชีเดือนเสร็จเพื่อป้องกันการแก้ไขย้อนหลัง',
          variant: 'info',
        },
        {
          kind: 'image',
          src: '/help/master/11-period.png',
          caption:
            'งวดบัญชี — สถานะเปิด/ปิด/ยังไม่เปิด พร้อมปุ่มปิดงวด-เปิดงวด และผู้ปิด/วันที่ปิด',
        },
      ],
    },
    {
      id: 'statuses',
      heading: '13. สรุปสถานะและป้ายกำกับ',
      blocks: [
        {
          kind: 'table',
          head: ['สถานะ / ป้าย', 'ความหมาย'],
          rows: [
            ['ใช้งาน', 'ข้อมูลที่เปิดใช้งานอยู่ (จุดเขียว)'],
            ['ปิด', 'ข้อมูลที่ปิดใช้งานแล้ว (เทา) — ไม่แสดงในตัวเลือกของหน้าอื่น'],
            ['งวด: เปิดอยู่', 'งวดบัญชีที่รับการบันทึกบัญชีได้'],
            ['งวด: ปิดงวดแล้ว', 'งวดที่ปิดบัญชีแล้ว — โพสต์ไม่ได้จนกว่าจะเปิดงวดใหม่'],
            ['งวด: ยังไม่เปิด', 'งวดในอนาคตที่ยังไม่เปิดให้บันทึก'],
            ['สกุลเงิน (ผู้ขาย)', 'ป้ายเตือนสีส้มเมื่อผู้ขายใช้สกุลเงินอื่นที่ไม่ใช่ THB'],
            ['การใช้วงเงิน (ลูกค้า)', 'เขียว < 60% · ส้ม 60–80% · แดง > 80% ของวงเงินเครดิต'],
          ],
        },
      ],
    },
    {
      id: 'faq',
      heading: '14. คำถามที่พบบ่อยและข้อควรระวัง',
      blocks: [
        { kind: 'para', text: 'ทำไมเปลี่ยนบริษัทแล้วข้อมูลในตารางเปลี่ยน?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'ข้อมูลหลักเกือบทุกประเภทแยกตามบริษัท (DCC/TTOP/RCI) ตารางจะแสดงเฉพาะข้อมูลของบริษัทที่กำลังเลือกใช้งาน — ยกเว้นบริษัทในเครือและสกุลเงินที่ใช้ร่วมกัน',
            },
          ],
        },
        { kind: 'para', text: 'ทำไมแก้ไข/ลบผังบัญชีหรือบริษัทไม่ได้?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'ผังบัญชีและบริษัทในเครือเป็นข้อมูลแบบดูอย่างเดียวในหน้านี้ · สกุลเงินแก้ไขได้เฉพาะชื่อสกุลเงิน ส่วนอัตราแลกเปลี่ยนเป็นแบบดูอย่างเดียว (แก้ไขด้วยมือไม่ได้)',
            },
          ],
        },
        { kind: 'para', text: 'ลบข้อมูลแล้วหายถาวรหรือไม่?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'การกด X เป็นการปิดใช้งาน (ซ่อนจากตัวเลือกของหน้าอื่น) ไม่ได้ลบประวัติที่เคยอ้างอิงไปแล้ว — ข้อมูลยังอยู่ในระบบ',
            },
          ],
        },
        { kind: 'para', text: 'ปิดงวดแล้วบันทึกบัญชีย้อนหลังไม่ได้?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'ถูกต้อง — ต้องกด “เปิดงวดใหม่” ที่หน้า ปีบัญชี & งวด ก่อน จึงจะบันทึกในงวดนั้นได้อีกครั้ง',
            },
          ],
        },
      ],
    },
    {
      id: 'glossary',
      heading: '15. อภิธานศัพท์',
      blocks: [
        {
          kind: 'bullets',
          items: [
            {
              lead: 'Master Data (ข้อมูลหลัก) — ',
              text: 'ข้อมูลตั้งต้นที่ระบบนำไปอ้างอิงซ้ำ ๆ เช่น ผังบัญชี สาขา ลูกค้า ผู้ขาย',
            },
            {
              lead: 'ลำดับที่ของสาขา — ',
              text: 'เลข 5 หลักที่จดทะเบียนสาขากับสรรพากร (00000 = สำนักงานใหญ่)',
            },
            {
              lead: 'Payment Terms (เงื่อนไขชำระ) — ',
              text: 'จำนวนวันให้เครดิต และส่วนลดจ่ายเร็ว เช่น สุทธิ 30 วัน',
            },
            { lead: 'Credit Limit (วงเงินเครดิต) — ', text: 'วงเงินสูงสุดที่ให้ลูกค้าค้างชำระได้' },
            {
              lead: 'บัญชีแยกประเภท GL — ',
              text: 'รหัสบัญชีในผังบัญชีที่ผูกกับบัญชีธนาคาร เพื่อให้รายการลงบัญชีถูกต้อง',
            },
            {
              lead: 'งวดบัญชี (Period) — ',
              text: 'เดือน/ปีบัญชีที่ควบคุมการเปิด-ปิดรับการบันทึกบัญชี',
            },
            {
              lead: 'FX Rate / สกุลเงิน — ',
              text: 'อัตราแลกเปลี่ยนของแต่ละสกุลเงินเทียบกับเงินบาท ใช้ในงานหลายสกุลเงิน',
            },
          ],
        },
      ],
    },
  ],
};
